/**
 * PrintTemplatesPanel — plantillas de impresión avanzadas (PLAN_SUTEX_Y_PLANTILLAS F1a).
 *
 * Pantalla mínima mientras llega el editor de arrastrar y soltar (F1c): papel,
 * tipos de documento y la definición en JSON, con vista previa sin guardar
 * sobre el ejemplo de cada variante del SII o sobre un documento emitido.
 *
 * Una versión publicada no se edita: para cambiarla se crea la siguiente. Sin
 * una publicada para el tipo, el PDF sale como siempre (D9).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, Eye, FileCode2, Plus } from 'lucide-react'
import { Button } from '@/components/ds/Button'
import { Drawer } from '@/components/ds/Drawer'
import { EmptyState } from '@/components/EmptyState'
import { SegmentGroup } from '@/components/ds/SegmentGroup'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { PRINT_TEMPLATE_STATUS } from '@/components/ds/statusCatalogs'
import { TextField } from '@/components/ds/TextField'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { getApiErrorMessage, getApiFieldErrors } from '@/lib/apiClient'
import { useSaleDocuments } from '@/modules/sales/hooks/useSales'
import {
  PreviewError,
  previewPrintTemplate,
  useCreatePrintTemplate,
  useDeletePrintTemplate,
  usePrintTemplate,
  usePrintTemplateAction,
  usePrintTemplateBases,
  usePrintTemplates,
  useUpdatePrintTemplate,
  type CopiaDeVistaPrevia,
  type PreviewVariant,
  type PrintTemplateSummary,
} from '@/modules/config/hooks/usePrintTemplates'
import { EditorVisual } from './editor-plantillas/EditorVisual'
import { SelectorDeTipos } from './editor-plantillas/SelectorDeTipos'
import {
  PAPELES, TIPOS_DTE, adaptarAlPapel, esRollo, nombreTipo, type Definicion, type Papel,
} from './editor-plantillas/geometria'

const VARIANTES: { value: PreviewVariant; label: string; hint: string }[] = [
  { value: 'documento', label: 'Factura', hint: 'Facturas, notas de crédito y débito, guías' },
  { value: 'boleta', label: 'Boleta', hint: 'Boletas: otro esquema del SII (RznSocEmisor, GiroEmisor)' },
  { value: 'exportacion', label: 'Exportación', hint: 'Tipos 110, 111 y 112' },
  { value: 'liquidacion', label: 'Liquidación', hint: 'Tipo 43' },
]

// PLAN §3.5: presupuesto de peso verificado en los tests con las bases.
const PRESUPUESTO_KB = { rollo: 40, hoja: 80 }


// Postgres guarda `jsonb` con su propio orden de claves (`id`, `h_mm`, `text`,
// `type`…): en pantalla se ordenan para que un elemento se lea de arriba abajo.
const ORDEN_DE_CLAVES = [
  'paper', 'font', 'elements', 'id', 'type', 'x_mm', 'y_mm', 'w_mm', 'h_mm', 'y_next_mm', 'bottom_mm',
  'pages', 'z', 'condition', 'text', 'path', 'formats', 'value', 'legend', 'header', 'width_pct', 'align',
  'columns', 'style', 'header_style',
]

function ordenar(valor: unknown): unknown {
  if (Array.isArray(valor)) return valor.map(ordenar)
  if (!valor || typeof valor !== 'object') return valor
  const posicion = (k: string) => (ORDEN_DE_CLAVES.includes(k) ? ORDEN_DE_CLAVES.indexOf(k) : ORDEN_DE_CLAVES.length)
  return Object.fromEntries(
    Object.entries(valor as Record<string, unknown>)
      .sort(([a], [b]) => posicion(a) - posicion(b) || a.localeCompare(b))
      .map(([k, v]) => [k, ordenar(v)]),
  )
}

const aTexto = (definicion: unknown) => JSON.stringify(ordenar(definicion), null, 2)

function leerJson(texto: string): Record<string, unknown> | null {
  try {
    const valor = JSON.parse(texto)
    return valor && typeof valor === 'object' && !Array.isArray(valor) ? valor : null
  } catch {
    return null
  }
}

/** «Todos los tipos» · «Factura, Boleta y 3 más»: el resumen corto de la plantilla. */
function resumenDeTipos(tipos: number[]): string {
  if (!tipos.length) return 'sin tipos'
  if (tipos.length === TIPOS_DTE.length) return 'Todos los tipos'
  const nombres = tipos.map(nombreTipo)
  return nombres.length <= 3 ? nombres.join(', ') : `${nombres.slice(0, 2).join(', ')} y ${nombres.length - 2} más`
}

// ─── Lista ───────────────────────────────────────────────────────────────────

interface Familia {
  family_id: string
  name: string
  publicada?: PrintTemplateSummary
  borrador?: PrintTemplateSummary
  ultima: PrintTemplateSummary
}

function agrupar(versiones: PrintTemplateSummary[]): Familia[] {
  const porFamilia = new Map<string, PrintTemplateSummary[]>()
  versiones.forEach(v => porFamilia.set(v.family_id, [...(porFamilia.get(v.family_id) ?? []), v]))
  return [...porFamilia.values()].map(vs => {
    const ordenadas = [...vs].sort((a, b) => b.version - a.version)
    return {
      family_id: ordenadas[0].family_id,
      name: ordenadas[0].name,
      publicada: ordenadas.find(v => v.status === 'published'),
      borrador: ordenadas.find(v => v.status === 'draft'),
      ultima: ordenadas[0],
    }
  }).sort((a, b) => a.name.localeCompare(b.name))
}

function NuevaPlantilla({ onCreada, onCancelar }: { onCreada: (id: string) => void; onCancelar: () => void }) {
  const { data: bases = [] } = usePrintTemplateBases()
  const crear = useCreatePrintTemplate()
  const [nombre, setNombre] = useState('')
  const [base, setBase] = useState('carta')
  const [tipos, setTipos] = useState<number[]>([33])
  const [orientacion, setOrientacion] = useState<'portrait' | 'landscape'>('portrait')
  const errores = getApiFieldErrors(crear.error)
  const elegida = bases.find(b => b.key === base)
  const papelBase = (elegida?.definition.paper ?? { preset: 'letter' }) as Papel
  const baseEsRollo = esRollo(papelBase.preset)

  const crearBorrador = () => {
    // Horizontal: la base se reacomoda en proporción, para que nada quede fuera de la hoja.
    const datos = orientacion === 'landscape' && elegida && !baseEsRollo
      ? { definition: adaptarAlPapel(elegida.definition as unknown as Definicion, { ...papelBase, orientation: 'landscape' }) as unknown as Record<string, unknown> }
      : { base }
    crear.mutate({ name: nombre, document_types: tipos, ...datos }, {
      onSuccess: p => onCreada(p.id),
      onError: e => { getApiErrorMessage(e) },
    })
  }

  return (
    <div className="p-4 bg-surface border border-divider rounded-card space-y-3 max-w-2xl">
      <p className="text-sm font-semibold text-content">Nueva plantilla avanzada</p>
      <TextField dense label="Nombre" value={nombre} onChange={e => setNombre(e.target.value)}
        placeholder="Factura carta con logo" error={errores.name?.[0]} />
      <div className="grid grid-cols-2 gap-3">
        <SelectField dense label="Partir de" value={base} onChange={e => setBase(e.target.value)}>
          {bases.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
        </SelectField>
        <SelectField dense label="Orientación" value={baseEsRollo ? 'portrait' : orientacion} disabled={baseEsRollo}
          hint={baseEsRollo ? 'Un rollo no tiene orientación.' : 'Se puede cambiar después en el editor.'}
          onChange={e => setOrientacion(e.target.value as 'portrait' | 'landscape')}>
          <option value="portrait">Vertical</option>
          <option value="landscape">Horizontal</option>
        </SelectField>
      </div>
      <SelectorDeTipos value={tipos} onChange={setTipos} error={errores.document_types?.[0]} />
      <div className="flex gap-2">
        <Button variant="primary" size="sm" loading={crear.isPending} disabled={!nombre.trim() || !tipos.length}
          onClick={crearBorrador}>
          Crear borrador
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancelar}>Cancelar</Button>
      </div>
    </div>
  )
}

// ─── Editor ──────────────────────────────────────────────────────────────────

function ListaDeErrores({ errores }: { errores: Record<string, string[]> }) {
  const entradas = Object.entries(errores)
  if (!entradas.length) return null
  return (
    <div role="alert" className="p-3 rounded-lg border border-danger-border bg-danger-surface text-xs space-y-1 max-h-48 overflow-y-auto">
      <p className="font-semibold text-danger-content">La plantilla tiene {entradas.length} error{entradas.length !== 1 ? 'es' : ''}</p>
      {entradas.map(([ruta, mensajes]) => (
        <p key={ruta} className="text-danger-content-soft">
          <span className="font-mono">{ruta.replace(/^definition\.?/, '') || 'plantilla'}</span>: {mensajes.join(' ')}
        </p>
      ))}
    </div>
  )
}

function SelectorDeDocumento({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const [busqueda, setBusqueda] = useState('')
  const { data } = useSaleDocuments(
    busqueda.trim().length >= 2 ? { search: busqueda.trim(), status: 'accepted', page_size: 6 } : undefined,
  )
  const resultados = busqueda.trim().length >= 2 ? data?.documents ?? [] : []
  return (
    <div className="space-y-1.5">
      <TextField dense label="Documento emitido (opcional)" value={busqueda}
        onChange={e => setBusqueda(e.target.value)} placeholder="Folio o cliente"
        hint={value ? 'Vista previa con el XML firmado de ese documento.' : 'Sin documento: se usa el ejemplo de la variante.'} />
      {resultados.length > 0 && (
        <ul className="border border-divider rounded-lg divide-y divide-divider bg-surface max-h-40 overflow-y-auto">
          {resultados.map(d => (
            <li key={d.id}>
              <button type="button" onClick={() => { onChange(d.id); setBusqueda(`${d.document_type_display} ${d.folio ?? ''}`) }}
                className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-soft ${value === d.id ? 'bg-primary-50' : ''}`}>
                <span className="font-medium text-content">{d.document_type_display} N° {d.folio ?? '—'}</span>
                <span className="text-content-subtle"> · {d.customer_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {value && <Button variant="ghost" size="sm" onClick={() => { onChange(''); setBusqueda('') }}>Usar el ejemplo</Button>}
    </div>
  )
}

function Editor({ id, onVolver, onAbrir }: { id: string; onVolver: () => void; onAbrir: (id: string) => void }) {
  const { data: plantilla, isLoading } = usePrintTemplate(id)
  const actualizar = useUpdatePrintTemplate()
  const accion = usePrintTemplateAction()
  const eliminar = useDeletePrintTemplate()
  const { confirm, modalProps } = useConfirmModal()

  const [nombre, setNombre] = useState('')
  const [tipos, setTipos] = useState<number[]>([])
  // La definición es la fuente; el texto JSON de «Avanzado» se deriva de ella y,
  // mientras se escribe a mano, puede quedar inválido sin perder lo del lienzo.
  const [definicion, setDefinicion] = useState<Definicion | null>(null)
  const [texto, setTexto] = useState('')
  const [jsonInvalido, setJsonInvalido] = useState(false)
  const [errores, setErrores] = useState<Record<string, string[]>>({})
  const [variante, setVariante] = useState<PreviewVariant>('documento')
  const [documentoId, setDocumentoId] = useState('')
  const [copiaVista, setCopiaVista] = useState<CopiaDeVistaPrevia>('original')
  const [vistaAbierta, setVistaAbierta] = useState(false)
  const [vista, setVista] = useState<{ url: string; bytes: number } | null>(null)
  const [generando, setGenerando] = useState(false)
  const [errorVista, setErrorVista] = useState('')

  useEffect(() => {
    if (!plantilla) return
    setNombre(plantilla.name)
    setTipos(plantilla.document_types)
    setDefinicion(plantilla.definition as unknown as Definicion)
    setTexto(aTexto(plantilla.definition))
    setJsonInvalido(false)
    setErrores({})
  }, [plantilla])
  useEffect(() => () => { if (vista) URL.revokeObjectURL(vista.url) }, [vista])

  const cambiarDefinicion = useCallback((d: Definicion) => {
    setDefinicion(d)
    setTexto(aTexto(d))
    setJsonInvalido(false)
  }, [])

  if (isLoading || !plantilla || !definicion) return <p className="text-sm text-content-subtle py-10 text-center">Cargando…</p>

  const papel = (definicion.paper ?? { preset: 'letter' }) as Papel
  const editable = plantilla.status === 'draft'
  const cambios = editable && (
    nombre !== plantilla.name
    || tipos.join() !== plantilla.document_types.join()
    || aTexto(definicion) !== aTexto(plantilla.definition)
  )
  const presupuesto = esRollo(papel.preset) || (papel.preset === 'custom' && papel.height_mm == null)
    ? PRESUPUESTO_KB.rollo : PRESUPUESTO_KB.hoja
  const kb = vista ? vista.bytes / 1024 : 0

  const conErrores = (e: unknown) => {
    setErrores(getApiFieldErrors(e))
    getApiErrorMessage(e)
  }

  const guardar = (despues?: () => void) => {
    actualizar.mutate({
      id, data: { name: nombre, document_types: tipos, definition: definicion as unknown as Record<string, unknown> },
    }, {
      onSuccess: () => { setErrores({}); despues?.() },
      onError: conErrores,
    })
  }

  const generarVista = async () => {
    setVistaAbierta(true)
    setGenerando(true)
    setErrorVista('')
    try {
      setVista(await previewPrintTemplate({
        definition: definicion, variant: variante, copy: copiaVista, ...(documentoId ? { document_id: documentoId } : {}),
      }))
    } catch (e) {
      if (e instanceof PreviewError) {
        setErrores(e.details)
        setErrorVista(e.message)
      }
    } finally {
      setGenerando(false)
    }
  }

  const publicar = () => {
    const tiposTexto = tipos.map(nombreTipo).join(', ')
    const superpuestas = plantilla.overlapping.length
      ? ` Reemplaza a ${plantilla.overlapping.map(o => `«${o.name}»`).join(', ')} en los tipos que comparten.`
      : ''
    confirm({
      message: `Publicar la versión ${plantilla.version} de «${nombre}». Desde ahora los PDF de ${tiposTexto} salen con esta plantilla.${superpuestas} Una versión publicada no se edita: para cambiarla se crea la siguiente.`,
      variant: 'warning',
      confirmLabel: 'Publicar',
      onConfirm: () => {
        const ejecutar = () => accion.mutate({ id, action: 'publish' }, { onError: conErrores })
        if (cambios) guardar(ejecutar)
        else ejecutar()
      },
    })
  }

  return (
    <div className="space-y-3">
      <ConfirmModal {...modalProps} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" icon={<ArrowLeft size={14} />} onClick={onVolver}>Volver a la lista</Button>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={plantilla.status} catalog={PRINT_TEMPLATE_STATUS} />
          <span className="text-xs text-content-subtle">Versión {plantilla.version}</span>
          <Button size="sm" icon={<Eye size={14} />} loading={generando} disabled={jsonInvalido} onClick={generarVista}>
            Vista previa PDF
          </Button>
          {editable ? (
            <>
              <Button size="sm" variant="danger" loading={eliminar.isPending}
                onClick={() => confirm({
                  message: `Eliminar el borrador de la versión ${plantilla.version}. Las versiones publicadas no se tocan.`,
                  variant: 'danger', confirmLabel: 'Eliminar',
                  onConfirm: () => eliminar.mutate(id, { onSuccess: onVolver, onError: conErrores }),
                })}>
                Eliminar borrador
              </Button>
              <Button size="sm" loading={actualizar.isPending} disabled={!cambios || jsonInvalido} onClick={() => guardar()}>
                Guardar borrador
              </Button>
              <Button size="sm" variant="primary" loading={accion.isPending} disabled={jsonInvalido || !tipos.length}
                onClick={publicar}>
                Publicar
              </Button>
            </>
          ) : (
            <>
              {plantilla.status === 'published' && (
                <Button size="sm" variant="danger" loading={accion.isPending}
                  onClick={() => confirm({
                    message: `Retirar «${plantilla.name}». Los PDF de ${plantilla.document_types.map(nombreTipo).join(', ')} vuelven a salir con la plantilla de siempre.`,
                    variant: 'danger', confirmLabel: 'Retirar',
                    onConfirm: () => accion.mutate({ id, action: 'retire' }, { onError: conErrores }),
                  })}>
                  Retirar
                </Button>
              )}
              <Button size="sm" variant="primary" loading={accion.isPending}
                onClick={() => accion.mutate({ id, action: 'new-version' }, {
                  onSuccess: p => onAbrir(p.id), onError: conErrores,
                })}>
                Crear la versión {plantilla.version + 1}
              </Button>
            </>
          )}
        </div>
      </div>

      {!editable && (
        <p className="text-xs text-content-muted bg-surface-muted border border-divider rounded-lg px-3 py-2">
          Una versión {plantilla.status === 'published' ? 'publicada' : 'retirada'} no se edita: para cambiarla se crea la siguiente como borrador.
        </p>
      )}
      {plantilla.overlapping.length > 0 && (
        <p className="flex items-start gap-2 text-xs text-warning-content bg-warning-surface border border-warning-border rounded-lg px-3 py-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            También cubren estos tipos: {plantilla.overlapping.map(o =>
              `«${o.name}» (${o.document_types.map(nombreTipo).join(', ')})`).join('; ')}. Se usa la publicada más reciente.
          </span>
        </p>
      )}

      <details className="rounded-lg border border-divider bg-surface px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium text-content">
          {nombre} · {resumenDeTipos(tipos)} · {PAPELES.find(p => p.value === papel.preset)?.label}
        </summary>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <TextField dense label="Nombre" value={nombre} disabled={!editable} onChange={e => setNombre(e.target.value)} />
          <SelectorDeTipos value={tipos} onChange={setTipos} disabled={!editable} />
        </div>
      </details>

      <ListaDeErrores errores={errores} />

      <EditorVisual definicion={definicion} onChange={cambiarDefinicion} tipos={tipos} editable={editable}
        variante={variante} onVariante={setVariante} onErrores={setErrores} />

      <details className="rounded-lg border border-divider bg-surface px-3 py-2">
        <summary className="cursor-pointer text-sm text-content-muted">Avanzado: definición en JSON</summary>
        <div className="mt-3 space-y-2">
          {jsonInvalido && (
            <p className="text-xs text-danger-content-soft">El JSON no es válido: el lienzo sigue con la última versión válida.</p>
          )}
          <TextField dense multiline label="Definición (JSON)" value={texto} readOnly={!editable}
            onChange={e => {
              setTexto(e.target.value)
              const leida = leerJson(e.target.value)
              setJsonInvalido(!leida)
              if (leida) setDefinicion(leida as unknown as Definicion)
            }}
            hint="Elementos posicionados en mm. Rutas del XML sin namespace, relativas al documento: {Encabezado/IdDoc/Folio|formato}."
            // La clase entera: `className` en inputProps pisa la del sistema. El alto
            // va explícito porque `.cl-input` le gana a `rows`.
            inputProps={{ spellCheck: false, className: 'cl-input cl-input--sm font-mono text-xs', style: { height: '28rem' } }} />
        </div>
      </details>

      <Drawer open={vistaAbierta} onClose={() => setVistaAbierta(false)} size={720} title="Vista previa del PDF"
        actions={vista && (
          <span className={`text-xs ${kb > presupuesto ? 'text-warning-content' : 'text-content-subtle'}`}>
            {kb.toFixed(0)} KB de {presupuesto} KB recomendados
          </span>
        )}>
        <div className="flex flex-col gap-3 h-full">
          <SegmentGroup label="Ejemplo de la vista previa" size="sm" value={variante} onChange={v => setVariante(v)} options={VARIANTES} />
          <SegmentGroup label="Copia" size="sm" value={copiaVista} onChange={setCopiaVista} options={[
            { value: 'original', label: 'Original' },
            { value: 'cedible', label: 'Cedible' },
            { value: 'ambas', label: 'Las dos', hint: 'La original y la cedible en un mismo PDF, para imprimir' },
          ]} />
          <SelectorDeDocumento value={documentoId} onChange={setDocumentoId} />
          <Button size="sm" icon={<Eye size={14} />} loading={generando} onClick={generarVista}>Actualizar</Button>
          <div className="flex-1 min-h-[480px] bg-surface-muted rounded-xl border border-divider overflow-hidden flex items-center justify-center">
            {vista ? (
              <iframe src={vista.url} className="w-full h-full min-h-[480px]" title="Vista previa de la plantilla" />
            ) : (
              <p className="text-sm text-content-subtle p-8 text-center">{errorVista || 'Generando…'}</p>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  )
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export function PrintTemplatesPanel() {
  const { data: versiones = [], isLoading } = usePrintTemplates()
  const [abierta, setAbierta] = useState<string | null>(null)
  const [creando, setCreando] = useState(false)
  const familias = useMemo(() => agrupar(versiones), [versiones])

  if (abierta) return <Editor id={abierta} onVolver={() => setAbierta(null)} onAbrir={setAbierta} />
  if (isLoading) return <p className="text-sm text-content-subtle py-10 text-center">Cargando…</p>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-content-muted">
          Elementos posicionados sobre el XML firmado. Sin una plantilla publicada para el tipo, el PDF sale como siempre.
        </p>
        {!creando && (
          <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setCreando(true)}>
            Nueva plantilla avanzada
          </Button>
        )}
      </div>

      {creando && <NuevaPlantilla onCancelar={() => setCreando(false)} onCreada={id => { setCreando(false); setAbierta(id) }} />}

      {familias.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {familias.map(f => (
            <button key={f.family_id} type="button" onClick={() => setAbierta((f.borrador ?? f.publicada ?? f.ultima).id)}
              className="text-left p-4 bg-surface rounded-card border border-divider hover:border-primary-200 hover:shadow-sm transition-all shadow-card">
              <p className="text-sm font-medium text-content truncate">{f.name}</p>
              <p className="text-xs text-content-subtle mt-1 line-clamp-2">
                {resumenDeTipos(f.ultima.document_types)}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                {f.publicada && <StatusBadge status="published" catalog={PRINT_TEMPLATE_STATUS} label={`Publicada v${f.publicada.version}`} />}
                {f.borrador && <StatusBadge status="draft" catalog={PRINT_TEMPLATE_STATUS} label={`Borrador v${f.borrador.version}`} />}
                {!f.publicada && !f.borrador && <StatusBadge status="retired" catalog={PRINT_TEMPLATE_STATUS} />}
              </div>
            </button>
          ))}
        </div>
      ) : !creando && (
        <EmptyState icon={FileCode2} title="No hay plantillas avanzadas"
          hint="Partir de la base de carta o de rollo de 80 mm y ajustarla. Hasta publicarla, los PDF no cambian." size="md" />
      )}
    </div>
  )
}
